/**
 * Auth Context
 * Kitchly v0.5.0
 *
 * Manages user state, authentication, group context.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, setTokens, clearTokens, setGroupId, getGroupId } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(getGroupId());
  const [loading, setLoading] = useState(true);

  // Load user on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('kitchly_access_token');
    if (token) {
      authApi.me()
        .then(data => {
          setUser(data.user);
          setGroups(data.groups || []);
          // If no active group set but user has groups, use the first
          if (!getGroupId() && data.groups?.length > 0) {
            const firstGroupId = String(data.groups[0].id);
            setActiveGroupId(firstGroupId);
            setGroupId(firstGroupId);
          }
        })
        .catch(() => {
          clearTokens();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login({ email, password });
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setGroups(data.groups || []);

    if (data.groups?.length > 0) {
      const firstGroupId = String(data.groups[0].id);
      setActiveGroupId(firstGroupId);
      setGroupId(firstGroupId);
    } else {
      setActiveGroupId(null);
      setGroupId(null);
    }

    return data;
  }, []);

  const register = useCallback(async (email, password, displayName, inviteCode) => {
    const data = await authApi.register({
      email,
      password,
      display_name: displayName,
      invite_code: inviteCode
    });
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setGroups([]);
    setActiveGroupId(null);
    setGroupId(null);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setGroups([]);
    setActiveGroupId(null);
  }, []);

  const switchGroup = useCallback((groupId) => {
    const id = groupId ? String(groupId) : null;
    setActiveGroupId(id);
    setGroupId(id);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.me();
      setUser(data.user);
      setGroups(data.groups || []);
      return data;
    } catch {
      return null;
    }
  }, []);

  const value = {
    user,
    groups,
    activeGroupId,
    loading,
    login,
    register,
    logout,
    switchGroup,
    refreshUser,
    isSolo: !activeGroupId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
